/**
 * Planning Agent sessions — thin CRUD over `content_brief_plans` rows
 * so a single Piece can host multiple, switchable Planning conversations
 * (parity with AI Chat's `composer_chat_threads`).
 *
 * A "session" here is one `content_brief_plans` row scoped to
 * (workspace, brief/piece). The active session is selected via URL hash
 * `#plan=<planId>` on /composer; when absent, the newest non-archived
 * row wins so existing single-session Pieces keep working unchanged.
 */
import { supabase } from "@/integrations/supabase/client";

export type PlanningSession = {
  id: string;
  workspace_id: string;
  brief_id: string;
  title: string | null;
  archived_at: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  intake_state: Record<string, unknown> | null;
};

const SESSION_COLUMNS =
  "id, workspace_id, brief_id, title, archived_at, status, created_at, updated_at, intake_state";

/** Read `#plan=<id>` from `window.location.hash`. */
export function readActivePlanIdFromHash(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(raw);
  const v = params.get("plan");
  return v && v.length > 0 ? v : null;
}

/** Write `#plan=<id>` without a history entry. Preserves other hash params. */
export function writeActivePlanIdToHash(planId: string | null): void {
  if (typeof window === "undefined") return;
  const raw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(raw);
  if (planId) params.set("plan", planId);
  else params.delete("plan");
  const next = params.toString();
  const url = `${window.location.pathname}${window.location.search}${next ? `#${next}` : ""}`;
  window.history.replaceState(null, "", url);
  // Notify listeners (hashchange doesn't fire for replaceState).
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

export async function listPlanningSessions(
  workspaceId: string,
  briefId: string,
): Promise<PlanningSession[]> {
  const { data, error } = await supabase
    .from("content_brief_plans")
    .select(SESSION_COLUMNS)
    .eq("workspace_id", workspaceId)
    .eq("brief_id", briefId)
    .order("updated_at", { ascending: false });
  if (error) throw new Error(`Failed to load planning sessions: ${error.message}`);
  return (data ?? []) as unknown as PlanningSession[];
}

export async function createPlanningSession(
  workspaceId: string,
  briefId: string,
  title?: string | null,
): Promise<PlanningSession> {
  const {
    data: { user },
    error: userErr,
  } = await supabase.auth.getUser();
  if (userErr) throw new Error(`Auth error: ${userErr.message}`);
  if (!user) throw new Error("Sign in to create a Planning session.");

  const { data, error } = await supabase
    .from("content_brief_plans")
    .insert({
      workspace_id: workspaceId,
      brief_id: briefId,
      requested_by_user_id: user.id,
      status: "running",
      title: title?.trim() || null,
      intake_state: { transcript: [], checklist: null, approved_brief: null },
    })
    .select(SESSION_COLUMNS)
    .single();
  if (error) throw new Error(`Failed to create Planning session: ${error.message}`);
  return data as unknown as PlanningSession;
}

export async function renamePlanningSession(planId: string, title: string): Promise<void> {
  const clean = title.trim();
  if (!clean) throw new Error("Title can't be empty.");
  const { error } = await supabase
    .from("content_brief_plans")
    .update({ title: clean })
    .eq("id", planId);
  if (error) throw new Error(`Failed to rename session: ${error.message}`);
}

export async function archivePlanningSession(planId: string, archived: boolean): Promise<void> {
  const { error } = await supabase
    .from("content_brief_plans")
    .update({ archived_at: archived ? new Date().toISOString() : null })
    .eq("id", planId);
  if (error) throw new Error(`Failed to ${archived ? "archive" : "unarchive"} session: ${error.message}`);
}

export async function deletePlanningSession(planId: string): Promise<void> {
  const { error } = await supabase.from("content_brief_plans").delete().eq("id", planId);
  if (error) throw new Error(`Failed to delete session: ${error.message}`);
}

/** Short label for a session — falls back to intake transcript's first user line. */
export function planningSessionLabel(s: PlanningSession): string {
  const t = s.title?.trim();
  if (t) return t;
  const transcript = (s.intake_state as { transcript?: Array<{ role?: string; content?: unknown }> } | null)
    ?.transcript;
  if (Array.isArray(transcript)) {
    const firstUser = transcript.find((m) => m?.role === "user");
    const raw =
      typeof firstUser?.content === "string"
        ? firstUser.content
        : Array.isArray(firstUser?.content)
          ? firstUser.content
              .map((p: unknown) =>
                typeof (p as { text?: unknown })?.text === "string" ? (p as { text: string }).text : "",
              )
              .join(" ")
          : "";
    const snippet = raw.trim();
    if (snippet) return snippet.length > 60 ? `${snippet.slice(0, 57)}…` : snippet;
  }
  return "Untitled session";
}
